import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { BaseUrl } from "../../BaseUrl";
import { Eye, EyeOff } from "lucide-react";

const DoctorLogin = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [loginWithOtp, setLoginWithOtp] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpType, setOtpType] = useState("");
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  // Detect email or mobile
  const detectType = (value) => {
    if (/^[0-9]{10}$/.test(value)) return "mobile";
    return "email";
  };

  // Timer logic
  useEffect(() => {
    let interval;
    if (otpSent && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  // Send OTP
  const sendOtp = async () => {
    try {
      if (!email) return toast.error("Enter email or mobile");

      const type = detectType(email);
      const payload =
        type === "mobile"
          ? { mobileNumber: email }
          : { email: email };

      await axios.post(`${BaseUrl}/doctor/send-otp`, payload);

      setOtpType(type);
      setOtpSent(true);
      setTimer(30);
      setCanResend(false);

      toast.success(`OTP sent successfully to your ${type}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    }
  };

  // Verify OTP login
  const verifyOtpLogin = async () => {
    try {
      if (otp.length !== 6) {
        return toast.error("Enter valid 6-digit OTP");
      }

      const payload =
        otpType === "mobile"
          ? { mobileNumber: email, otp }
          : { email: email, otp };

      const res = await axios.post(
        `${BaseUrl}/doctor/verify-login-otp`,
        payload,
        { withCredentials: true }
      );

      toast.success("Login successful");

      const token = res.data.data?.token;
      const user = res.data.data;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
      }

      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
    }
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loginWithOtp) {
      return verifyOtpLogin();
    }

    try {
      setLoading(true);

      const response = await axios.post(
        `${BaseUrl}/doctor/doctorLogin`,
        { email, password, role: "alpha" },
        { withCredentials: true }
      );

      toast.success(response.data.message || "Login Successful");

      const token = response.data.token || response.data.data?.token;
      const user = response.data.data || response.data;

      if (token) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));
      }

      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex items-center justify-center p-2 sm:p-4 bg-gray-100">
      <div className="w-full max-w-4xl lg:max-w-5xl rounded-lg border sm:p-6 md:p-10 lg:p-6 bg-white shadow-[0px_10px_40px_rgba(0,0,0,0.2)] flex flex-col lg:flex-row">

        {/* Left Section */}
        <div className="w-full lg:w-4/5 pr-0 lg:pr-6 mb-4 lg:mb-0 hidden md:block">
          <h4 className="text-base sm:text-lg md:text-xl font-semibold text-gray-600 mb-4">
            One Stop Healthcare Facility management Software as a service for Clinics & more Get Everything in One
          </h4>
          <ul className="list-disc list-inside text-gray-700 space-y-2 text-sm sm:text-base">
            <li>AI-powered operations</li>
            <li>Facility marketing tools</li>
            <li>Smart Calling system</li>
            <li>Patient relationship</li>
            <li>Secure Data</li>
            <li>Facility Linkage</li>
            <li>More Profit & Growth</li>
          </ul>
        </div>

        {/* Right Section */}
        <div className="w-full lg:w-3/5 pt-4 lg:pt-0 lg:pl-6 rounded-[8px] shadow-[0px_10px_20px_rgba(0,0,0,0.2)] p-3 sm:p-4 md:p-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">

            {/* Input */}
            <div>
              <label className="block mt-5 font-medium text-gray-700 text-sm sm:text-base">
                Mobile Number / HF ID / Email ID
              </label>
              <input
                type="text"
                placeholder="Enter email, mobile, or ID"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border p-2.5 sm:p-3"
              />
            </div>

            {/* Password / OTP */}
            {!loginWithOtp ? (
              <div>
                <label className="block font-medium text-gray-700 text-sm sm:text-base">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full rounded-lg border p-2.5 sm:p-3 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <label className="block font-medium text-gray-700 text-sm sm:text-base">
                  Enter OTP
                </label>

                <div className="flex items-center gap-2 flex-nowrap">
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={otp}
                    onChange={(e) =>
                      setOtp(e.target.value.replace(/[^0-9]/g, ""))
                    }
                    maxLength={6}
                    className="mt-1 flex-1 min-w-0 rounded-lg border p-2.5 sm:p-3"
                  />

                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={otpSent && !canResend}
                    className="mt-1 px-4 py-4 bg-[#0095D9] text-white rounded-lg text-sm whitespace-nowrap disabled:opacity-50"
                  >
                    {otpSent && !canResend ? `${timer}s` : "Send OTP"}
                  </button>
                </div>
              </div>
            )}

            {/* Remember + Forgot */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs sm:text-sm text-gray-400">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="mr-2"
                />
                <span>Remember me for 30 days</span>
              </label>

              {!loginWithOtp && (
                <Link
                  to="/forgot-password"
                  className="text-[#0095D9] hover:underline"
                >
                  Forgot password?
                </Link>
              )}
            </div>

            {/* Toggle */}
            <div>
              <button
                type="button"
                onClick={() => {
                  setLoginWithOtp(!loginWithOtp);
                  setOtp("");
                  setOtpSent(false);
                }}
                className="text-[#0095D9] text-xs sm:text-sm font-medium"
              >
                {loginWithOtp ? "Login with Password" : "Login with OTP"}
              </button>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-[#0095D9] py-2.5 sm:py-3 text-white"
            >
              {loading ? "Loading..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default DoctorLogin;